/**
 * Typed fetch wrapper for every route in @hub/shared/api. The token and server URL come from the session store
 * (configured by src/app/session.ts); nothing here talks to the engine.
 */
import type {
  AcceptInviteRequest,
  GeocodeResult,
  ApiKeysResponse,
  CreateApiKeyRequest,
  CreateApiKeyResponse,
  CreateWebhookRequest,
  ConnectedAccount,
  ConnectedAccountProvider,
  ConnectedAccountsResponse,
  DeleteAccountRequest,
  SessionsResponse,
  WebhookInfo,
  CreateWebhookResponse,
  WebhooksResponse,
  ConnectionRequestBody,
  ConnectionsResponse,
  ContactSharesResponse,
  CreateContactShareRequest,
  CreateWorkspaceRequest,
  CreditsResponse,
  DailyFocusResponse,
  DirectoryQuery,
  DirectoryResponse,
  DiscoverMatchesResponse,
  FacilitateRequest,
  Introduction,
  InviteMemberRequest,
  InviteMemberResponse,
  WorkspaceInvitePreview,
  InviteToHubRequest,
  InviteToHubResponse,
  LinkContactsRequest,
  LinkContactsResponse,
  PeopleYouKnowResponse,
  MatchActionRequest,
  MatchActionResponse,
  MatchCardsResponse,
  MatchReviewRequest,
  MeResponse,
  Message,
  OAuthStartRequest,
  OAuthStartResponse,
  MessagesResponse,
  Connection,
  ContactShare,
  ConversationsResponse,
  OpportunitiesResponse,
  RequestCodeResponse,
  RequestIntroductionRequest,
  RespondConnectionBody,
  RespondShareBody,
  RespondShareResponse,
  SendMessageRequest,
  SetSelfContactRequest,
  SetSelfContactResponse,
  StartConversationRequest,
  SyncPullRequest,
  SyncPullResponse,
  SyncPushRequest,
  SyncPushResponse,
  UpdateConnectionBody,
  UpsertNeedsRequest,
  UpsertServicesRequest,
  VerifyCodeRequest,
  VerifyCodeResponse,
  Workspace,
  WorkspaceMembersResponse,
  WorkspaceRole,
  Device,
  Conversation,
  ChangeEmailRequest,
  ChangeEmailResponse,
  AdminAccount,
  AdminFeedbackResponse,
  AdminInviteRequest,
  AdminMatchesResponse,
  AskFeedbackRequest,
  SendFeedbackRequest,
} from "@hub/shared";
import { DEFAULT_SERVER_URL } from "@hub/shared";

export class ApiError extends Error {
  constructor(message: string, public readonly status: number, public readonly code: string, public readonly details: unknown = null) {
    super(message);
    this.name = "ApiError";
  }
  get isNetwork() {
    return this.status === 0;
  }
  get isUnauthorized() {
    return this.status === 401;
  }
}

interface ApiConfig {
  getToken: () => string | null;
  getServerUrl: () => string;
  onUnauthorized?: () => void;
  fetch?: typeof fetch;
}

let config: ApiConfig = {
  getToken: () => safeGet("hub_token"),
  getServerUrl: () => safeGet("hub_server_url") || DEFAULT_SERVER_URL,
};

function safeGet(key: string): string | null {
  try {
    return typeof localStorage !== "undefined" ? localStorage.getItem(key) : null;
  } catch {
    return null;
  }
}

export function configureApi(next: Partial<ApiConfig>) {
  config = { ...config, ...next };
}

async function request<T>(method: string, path: string, body?: unknown, opts: { auth?: boolean; query?: Record<string, string | number | undefined> } = {}): Promise<T> {
  const base = config.getServerUrl().replace(/\/$/, "");
  const url = new URL(`${base}/api/v1${path}`);
  for (const [k, v] of Object.entries(opts.query ?? {})) if (v !== undefined && v !== "") url.searchParams.set(k, String(v));
  const headers: Record<string, string> = { Accept: "application/json" };
  if (body !== undefined) headers["Content-Type"] = "application/json";
  const token = opts.auth === false ? null : config.getToken();
  if (token) headers.Authorization = `Bearer ${token}`;
  let res: Response;
  try {
    res = await (config.fetch ?? fetch)(url.toString(), { method, headers, body: body === undefined ? undefined : JSON.stringify(body) });
  } catch (e) {
    throw new ApiError(e instanceof Error ? e.message : "Network error", 0, "network");
  }
  if (res.status === 204) return undefined as T;
  const text = await res.text();
  let json: unknown = null;
  try {
    json = text ? JSON.parse(text) : null;
  } catch {
    json = null;
  }
  if (!res.ok) {
    const err = (json as { error?: { code?: string; message?: string; details?: unknown } } | null)?.error;
    if (res.status === 401) config.onUnauthorized?.();
    throw new ApiError(err?.message ?? `Request failed (${res.status})`, res.status, err?.code ?? `http_${res.status}`, err?.details ?? null);
  }
  return json as T;
}

const get = <T>(path: string, query?: Record<string, string | number | undefined>) => request<T>("GET", path, undefined, { query });
const post = <T>(path: string, body?: unknown, opts?: { auth?: boolean }) => request<T>("POST", path, body ?? {}, opts);
const put = <T>(path: string, body?: unknown) => request<T>("PUT", path, body);
const patch = <T>(path: string, body?: unknown) => request<T>("PATCH", path, body);
const del = <T>(path: string, body?: unknown) => request<T>("DELETE", path, body);

export const api = {
  health: () => get<{ ok: true; version?: string }>("/health"),

  // auth
  requestCode: (email: string) => post<RequestCodeResponse>("/auth/request-code", { email }, { auth: false }),
  verifyCode: (email: string, code: string, device: { name: string; platform: Device["platform"] }) => post<VerifyCodeResponse>("/auth/verify", { email, code, device } satisfies VerifyCodeRequest, { auth: false }),
  me: () => get<MeResponse>("/me"),
  logout: () => post<void>("/auth/logout"),

  // workspaces
  workspaces: {
    list: () => get<{ workspaces: (Workspace & { role: WorkspaceRole })[] }>("/workspaces"),
    create: (body: CreateWorkspaceRequest) => post<{ workspace: Workspace }>("/workspaces", body),
    members: (workspaceId: string) => get<WorkspaceMembersResponse>(`/workspaces/${encodeURIComponent(workspaceId)}/members`),
    invite: (workspaceId: string, body: InviteMemberRequest) => post<InviteMemberResponse>(`/workspaces/${encodeURIComponent(workspaceId)}/invites`, body),
    setSelfContact: (workspaceId: string, body: SetSelfContactRequest) => patch<SetSelfContactResponse>(`/workspaces/${encodeURIComponent(workspaceId)}/members/me`, body),
    preview: (token: string) => get<WorkspaceInvitePreview>(`/invites/${encodeURIComponent(token)}`),
    acceptInvite: (body: AcceptInviteRequest) => post<{ workspace: Workspace & { role: WorkspaceRole } }>("/invites/accept", body),
  },

  // sync
  syncPush: (body: SyncPushRequest) => post<SyncPushResponse>("/sync/push", body),
  syncPull: (body: SyncPullRequest) => post<SyncPullResponse>("/sync/pull", body),

  network: {
    directory: (q: DirectoryQuery = {}) => get<DirectoryResponse>("/network/directory", { q: q.q, category: q.category, limit: q.limit }),
    linkContacts: (body: LinkContactsRequest) => post<LinkContactsResponse>("/network/link-contacts", body),
    peopleYouKnow: () => get<PeopleYouKnowResponse>("/network/people-you-know"),
    dismissPersonYouKnow: (userId: string) => post<{ ok: true }>(`/network/people-you-know/${encodeURIComponent(userId)}/dismiss`, {}),
    block: (userId: string) => post<{ ok: true }>("/network/blocks", { userId }),
    unblock: (userId: string) => del<{ ok: true }>(`/network/blocks/${encodeURIComponent(userId)}`),
    report: (body: { userId: string; reason: "spam" | "harassment" | "impersonation" | "inappropriate" | "other"; details?: string | null; conversationId?: string | null }) => post<{ id: string }>("/reports", body),
    connections: {
      list: () => get<ConnectionsResponse>("/network/connections"),
      request: (body: ConnectionRequestBody) => post<{ connection: Connection }>("/network/connections", body),
      respond: (id: string, body: RespondConnectionBody) => post<{ connection: Connection }>(`/network/connections/${encodeURIComponent(id)}/respond`, body),
      update: (id: string, body: UpdateConnectionBody) => patch<{ connection: Connection }>(`/network/connections/${encodeURIComponent(id)}`, body),
    },
    upsertNeeds: (body: UpsertNeedsRequest) => put<{ updated: number }>("/network/needs", body),
    upsertServices: (body: UpsertServicesRequest) => put<{ updated: number }>("/network/services", body),
    matches: () => get<MatchCardsResponse>("/network/matches"),
    discover: () => post<DiscoverMatchesResponse>("/network/matches/discover"),
    matchAction: (id: string, body: MatchActionRequest) => post<MatchActionResponse>(`/network/matches/${encodeURIComponent(id)}/actions`, body),
    matchReview: (id: string, body: MatchReviewRequest) => post<{ ok: true }>(`/network/matches/${encodeURIComponent(id)}/review`, body),
    opportunities: () => get<OpportunitiesResponse>("/network/opportunities"),
    facilitate: (body: FacilitateRequest) => post<{ introduction: Introduction }>("/network/facilitate", body),
    requestIntroduction: (body: RequestIntroductionRequest) => post<{ introduction: Introduction }>("/network/introductions", body),
    shares: {
      list: () => get<ContactSharesResponse>("/network/shares"),
      create: (body: CreateContactShareRequest) => post<{ shares: ContactShare[] }>("/network/shares", body),
      respond: (id: string, body: RespondShareBody) => post<RespondShareResponse>(`/network/shares/${encodeURIComponent(id)}/respond`, body),
    },
  },

  conversations: {
    list: () => get<ConversationsResponse>("/messages/conversations"),
    start: (body: StartConversationRequest) => post<{ conversation: Conversation; message: Message }>("/messages/conversations", body),
    messages: (id: string) => get<MessagesResponse>(`/messages/conversations/${encodeURIComponent(id)}`),
    send: (id: string, body: SendMessageRequest) => post<{ message: Message }>(`/messages/conversations/${encodeURIComponent(id)}`, body),
  },

  credits: () => get<CreditsResponse>("/credits"),

  // Google and Microsoft 365 accounts connected on the server (every device sees their rows through sync)
  integrations: {
    accounts: () => get<ConnectedAccountsResponse>("/integrations/accounts"),
    start: (provider: ConnectedAccountProvider, body: OAuthStartRequest) => post<OAuthStartResponse>(`/integrations/oauth/${provider}/start`, body),
    sync: (id: string, full = false) => post<{ account: ConnectedAccount }>(`/integrations/accounts/${encodeURIComponent(id)}/sync`, { full }),
    disconnect: (id: string, deleteData: boolean) => del<{ ok: true }>(`/integrations/accounts/${encodeURIComponent(id)}${deleteData ? "?deleteData=1" : ""}`),
  },
  focus: { today: () => get<DailyFocusResponse>("/focus/today") },
  /** Positions for cities and postal codes, from the server's shared cache (the browser has no geocoder of its own). */
  places: { lookup: (queries: string[]) => post<{ results: GeocodeResult[] }>("/places/lookup", { queries }) },
  invites: { toHub: (body: InviteToHubRequest) => post<InviteToHubResponse>("/invites", body) },

  // account hardening and developer access (routes may be missing on older servers: callers treat 404 as "not available")
  account: {
    sessions: () => get<SessionsResponse>("/me/sessions"),
    revokeSession: (id: string) => del<void>(`/me/sessions/${encodeURIComponent(id)}`),
    apiKeys: () => get<ApiKeysResponse>("/me/api-keys"),
    createApiKey: (body: CreateApiKeyRequest) => post<CreateApiKeyResponse>("/me/api-keys", body),
    revokeApiKey: (id: string) => del<void>(`/me/api-keys/${encodeURIComponent(id)}`),
    webhooks: () => get<WebhooksResponse>("/me/webhooks"),
    createWebhook: (body: CreateWebhookRequest) => post<CreateWebhookResponse>("/me/webhooks", body),
    deleteWebhook: (id: string) => del<void>(`/me/webhooks/${encodeURIComponent(id)}`),
    requestEmailCode: (email: string) => post<RequestCodeResponse>("/me/email/request-code", { email }),
    changeEmail: (body: ChangeEmailRequest) => post<ChangeEmailResponse>("/me/email", body),
    deleteAccount: (body: DeleteAccountRequest) => del<void>("/me", body),
  },

  feedback: {
    ask: (body: AskFeedbackRequest) => post<{ ok: true }>("/feedback/ask", body),
    send: (body: SendFeedbackRequest) => post<{ id: string }>("/feedback", body),
  },

  // The owner's routes: open to the accounts in the server's HUB_ADMIN_EMAILS, 403 for everyone else.
  admin: {
    accounts: () => get<{ users: AdminAccount[] }>("/admin/users"),
    updateAccount: (id: string, body: { planType?: string; suspended?: boolean }) => patch<{ user: AdminAccount; sessionsRevoked: number; apiKeysRevoked: number }>(`/admin/users/${encodeURIComponent(id)}`, body),
    inviteRequests: (status?: AdminInviteRequest["status"]) => get<{ requests: AdminInviteRequest[] }>("/invite-requests", { status }),
    updateInviteRequest: (id: string, body: { status?: AdminInviteRequest["status"]; note?: string | null }) => patch<{ request: AdminInviteRequest; emailed: boolean }>(`/invite-requests/${encodeURIComponent(id)}`, body),
    invite: (email: string, note?: string | null) => post<{ request: AdminInviteRequest; emailed: boolean }>("/invite-requests/invite", { email, note: note || null }),
    matches: () => get<AdminMatchesResponse>("/admin/matches"),
    feedback: () => get<AdminFeedbackResponse>("/admin/feedback"),
    markFeedbackHandled: (id: string, handled: boolean) => patch<{ id: string; handledAt: string | null }>(`/admin/feedback/messages/${encodeURIComponent(id)}`, { handled }),
  },
};

/** True when the server answered 404 for a route it does not implement yet (older server builds). */
export function isUnavailableRoute(e: unknown): boolean {
  return e instanceof ApiError && e.status === 404;
}

export type HubApi = typeof api;
